import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import { Close, Security, Videocam, Mic } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { appColors } from "../../constants/theme";
import { writeMeetingPermissionConsent } from "../../utils/meetingMediaPermission";

/**
 * Görüşme içinde / lobide tek cihaz (kamera veya mikrofon) için izin modalı.
 * AppointmentJoinDialog metinleri ve stiliyle uyumlu.
 */
const MeetingDevicePermissionDialog = ({
  open,
  kind,
  appointmentId,
  onClose,
  onGranted,
  onDenied,
}) => {
  const { t } = useTranslation();
  const isCamera = kind === "video" || kind === "camera";

  const questionKey = isCamera
    ? "meeting_permission_camera_question"
    : "meeting_permission_microphone_question";
  const bodyText = isCamera
    ? t("meeting_permission_camera_consent_body")
    : t("meeting_permission_microphone_consent_body");

  const handleAllow = () => {
    if (appointmentId) {
      writeMeetingPermissionConsent(appointmentId, {
        [isCamera ? "cameraPermissionConsent" : "microphonePermissionConsent"]: true,
        source: "meetingInCallPermissionModal",
      });
    }
    onGranted?.();
  };

  const handleDeny = () => {
    if (appointmentId) {
      writeMeetingPermissionConsent(appointmentId, {
        [isCamera ? "cameraPermissionConsent" : "microphonePermissionConsent"]: false,
        source: "meetingInCallPermissionModal",
      });
    }
    onDenied?.({ browserDenied: false });
  };

  const handleClose = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxHeight: "min(92vh, 720px)",
          overflowY: "auto",
          width: "100%",
          maxWidth: "min(calc(100vw - 32px), 420px)",
          mx: "auto",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1,
          pb: 0,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${appColors.tealBg} 0%, ${appColors.tealBorder} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Security sx={{ fontSize: 22, color: appColors.primary }} />
          </Box>
          <Typography
            component="span"
            variant="h6"
            sx={{ color: appColors.gray900, fontWeight: 700, fontSize: "1.05rem" }}
          >
            {t("meeting_permission_title")}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose} aria-label={t("cancel")}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", pt: 0, pb: 2, px: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, mt: 1.5 }}>
          {isCamera ? (
            <Videocam sx={{ fontSize: 20, color: appColors.primaryDark }} />
          ) : (
            <Mic sx={{ fontSize: 20, color: appColors.primaryDark }} />
          )}
          <Typography variant="subtitle1" sx={{ color: appColors.gray900, fontWeight: 700 }}>
            {t(questionKey)}
          </Typography>
        </Box>

        <Box
          sx={{
            borderRadius: "10px",
            bgcolor: appColors.gray50,
            border: `1px solid ${appColors.gray200}`,
            px: 1.75,
            py: 1.25,
            mb: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: appColors.gray500,
              fontSize: "0.8125rem",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {bodyText}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleDeny}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              borderColor: appColors.gray200,
              color: appColors.gray900,
              fontWeight: 600,
              px: 2,
              "&:hover": { borderColor: appColors.gray500, bgcolor: appColors.gray50 },
            }}
          >
            {t("meeting_permission_allow_no")}
          </Button>
          <Button
            variant="contained"
            onClick={handleAllow}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              fontWeight: 600,
              px: 2,
              background: `linear-gradient(135deg, ${appColors.primary} 0%, ${appColors.primaryTeal} 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, ${appColors.primaryDark} 0%, ${appColors.primary} 100%)`,
              },
            }}
          >
            {t("meeting_permission_allow_yes")}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MeetingDevicePermissionDialog;
